import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
    HiOutlineChatBubbleLeftRight,
    HiOutlineHeart,
    HiOutlineCurrencyDollar,
    HiOutlineCalendarDays,
    HiOutlineNoSymbol,
    HiOutlineCpuChip,
} from "react-icons/hi2";
import {
    Field,
    FormInput,
    FormTextarea,
    FormSelect,
    FormSwitch,
    SwitchGroup,
    Grid2,
    Grid3,
    CollapsibleSection,
    ItemCard,
    ItemHeader,
    AddButton,
    Divider,
    SubsectionTitle,
    WeightField,
    AdditionalInfoField,
    isFieldInvalid,
    isFieldWarning,
} from "../ui";
import {
    useJobOfferEnums,
    createEmptyCustomQuestion,
    createEmptyMotivationQuestion,
    createEmptyDisqualificationRule,
} from "@/models/jobOffer";
import { ScoringConfigPanel } from "@/scoringUi";
import { useScoringActive } from "@/auth/ScoringContext";
import { buildScoringLabels } from "../scoringLabels";
import InheritedCriteriaSection from "../InheritedCriteriaSection";

const DISQUALIFICATION_EXAMPLE_KEYS = ["noDrivingLicense", "missingRequiredLicenses", "noShiftWorkConsent", "noOvertimeConsent", "financialExpectationsAboveBudget"];

const CATEGORY_MAX_POINTS_ROWS = [
    { key: "documents" },
    { key: "criticalSkills" },
    { key: "optionalSkills" },
    { key: "experience" },
    { key: "availability" },
    { key: "financialExpectations" },
    { key: "motivation" },
    { key: "safety" },
    { key: "overtimeAndShifts" },
    { key: "customQuestions" },
];

const Step5AIConfig = ({ formik, warningErrors, visited, forceOpen, onScoringModelLoaded }) => {
    const scoringActive = useScoringActive();
    const { t } = useTranslation("jobOffer");
    const { CUSTOM_QUESTION_ANSWER_TYPE, CONVERSATION_STYLE, CONVERSATION_LANGUAGE, CURRENCY } = useJobOfferEnums();
    const { values, setFieldValue } = formik;
    const { customQuestions, motivationQuestions, financialExpectations, availability, disqualificationRules, aiConversationConfig, scoringConfig, scoringOverrides } = values;
    const invalid = (path) => isFieldInvalid(formik, path);
    const warn = (path) => isFieldWarning(warningErrors, visited, path);
    const sectionInfo = values.sectionAdditionalInfo ?? {};
    const setSectionInfo = (key, val) => setFieldValue(`sectionAdditionalInfo.${key}`, val);

    const updateCustomQuestion = (i, updates) =>
        setFieldValue("customQuestions", customQuestions.map((q, idx) => (idx === i ? { ...q, ...updates } : q)));
    const removeCustomQuestion = (i) => setFieldValue("customQuestions", customQuestions.filter((_, idx) => idx !== i));

    const updateMotivationQuestion = (i, updates) =>
        setFieldValue("motivationQuestions", motivationQuestions.map((q, idx) => (idx === i ? { ...q, ...updates } : q)));
    const removeMotivationQuestion = (i) => setFieldValue("motivationQuestions", motivationQuestions.filter((_, idx) => idx !== i));

    const updateDisqualificationRule = (i, updates) =>
        setFieldValue("disqualificationRules", disqualificationRules.map((r, idx) => (idx === i ? { ...r, ...updates } : r)));
    const removeDisqualificationRule = (i) => setFieldValue("disqualificationRules", disqualificationRules.filter((_, idx) => idx !== i));

    const scoringLabels = buildScoringLabels(t);

    const setFinancial = (updates) => setFieldValue("financialExpectations", { ...financialExpectations, ...updates });
    const setFinancialScoring = (scoring) => setFieldValue("financialExpectations.scoring", scoring);
    const setAvailability = (updates) => setFieldValue("availability", { ...availability, ...updates });
    const setAvailabilityScoring = (scoring) => setFieldValue("availability.scoring", scoring);
    const setAiConfig = (updates) => setFieldValue("aiConversationConfig", { ...aiConversationConfig, ...updates });
    const setCategoryMaxPoints = (key, value) =>
        setFieldValue("scoringConfig.categoryMaxPoints", { ...scoringConfig.categoryMaxPoints, [key]: value });

    return (
        <Box display="flex" flexDirection="column" gap={6}>
            {scoringActive && (
                <InheritedCriteriaSection
                    recruitmentId={values.id}
                    scenarioId={values.scenarioId}
                    scoringOverrides={scoringOverrides}
                    onOverridesChange={(next) => setFieldValue("scoringOverrides", next)}
                    onModelLoaded={onScoringModelLoaded}
                />
            )}

            {/* Section 12: Custom questions */}
            <CollapsibleSection
                number={12}
                title={t("step5.customQuestions.title")}
                subtitle={t("step5.customQuestions.subtitle")}
                icon={HiOutlineChatBubbleLeftRight}
                defaultOpen
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={3}>
                    {customQuestions.map((q, i) => (
                        <ItemCard key={i}>
                            <ItemHeader title={t("step5.customQuestions.itemTitle")} index={i} onRemove={() => removeCustomQuestion(i)} />
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Field label={t("step5.customQuestions.questionText")} required invalid={invalid(`customQuestions.${i}.questionText`)} warning={warn(`customQuestions.${i}.questionText`)}>
                                    <FormTextarea
                                        value={q.questionText}
                                        onChange={(e) => updateCustomQuestion(i, { questionText: e.target.value })}
                                        placeholder={t("step5.customQuestions.questionTextPlaceholder")}
                                        rows={2}
                                    />
                                </Field>
                                <Grid3>
                                    <Field label={t("step5.customQuestions.answerType")} required invalid={invalid(`customQuestions.${i}.answerType`)} warning={warn(`customQuestions.${i}.answerType`)}>
                                        <FormSelect value={q.answerType} onChange={(v) => updateCustomQuestion(i, { answerType: v })} placeholder={t("step5.customQuestions.answerTypePlaceholder")} options={CUSTOM_QUESTION_ANSWER_TYPE} />
                                    </Field>
                                    {false && <WeightField value={q.weight} onChange={(v) => updateCustomQuestion(i, { weight: v })} />}
                                </Grid3>
                                <Grid2>
                                    <Field label={t("step5.customQuestions.expectedAnswer")}>
                                        <FormInput value={q.expectedAnswer} onChange={(e) => updateCustomQuestion(i, { expectedAnswer: e.target.value })} placeholder={t("step5.customQuestions.expectedAnswerPlaceholder")} />
                                    </Field>
                                    <Field label={t("step5.customQuestions.eliminatingAnswer")}>
                                        <FormInput value={q.eliminatingAnswer} onChange={(e) => updateCustomQuestion(i, { eliminatingAnswer: e.target.value })} placeholder={t("step5.customQuestions.eliminatingAnswerPlaceholder")} />
                                    </Field>
                                </Grid2>
                                <SwitchGroup>
                                    <FormSwitch label={t("step5.customQuestions.required")} checked={q.isRequired} onChange={(v) => updateCustomQuestion(i, { isRequired: v })} />
                                    <FormSwitch label={t("step5.customQuestions.eliminating")} checked={q.isEliminating} onChange={(v) => updateCustomQuestion(i, { isEliminating: v })} />
                                    <FormSwitch label={t("step5.customQuestions.aiCanFollowUp")} checked={q.aiCanFollowUp} onChange={(v) => updateCustomQuestion(i, { aiCanFollowUp: v })} />
                                </SwitchGroup>
                                {q.aiCanFollowUp && (
                                    <Field label={t("step5.customQuestions.followUpQuestion")}>
                                        <FormInput value={q.followUpQuestionText} onChange={(e) => updateCustomQuestion(i, { followUpQuestionText: e.target.value })} placeholder={t("step5.customQuestions.followUpQuestionPlaceholder")} />
                                    </Field>
                                )}
                                {scoringActive && (
                                    <>
                                        <Divider my={1} />
                                        <ScoringConfigPanel
                                            config={q.scoring}
                                            onChange={(scoring) => updateCustomQuestion(i, { scoring })}
                                            labels={scoringLabels}
                                        />
                                    </>
                                )}
                            </Box>
                        </ItemCard>
                    ))}
                    <AddButton label={t("step5.customQuestions.add")} onClick={() => setFieldValue("customQuestions", [...customQuestions, createEmptyCustomQuestion()])} />
                </Box>
            </CollapsibleSection>

            {/* Section 13: Motivation */}
            <CollapsibleSection
                number={13}
                title={t("step5.motivation.title")}
                subtitle={t("step5.motivation.subtitle")}
                icon={HiOutlineHeart}
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={3}>
                    {motivationQuestions.map((q, i) => (
                        <ItemCard key={i}>
                            <ItemHeader title={t("step5.motivation.itemTitle")} index={i} onRemove={() => removeMotivationQuestion(i)} />
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Field label={t("step5.customQuestions.questionText")} required invalid={invalid(`motivationQuestions.${i}.questionText`)} warning={warn(`motivationQuestions.${i}.questionText`)}>
                                    <FormTextarea
                                        value={q.questionText}
                                        onChange={(e) => updateMotivationQuestion(i, { questionText: e.target.value })}
                                        placeholder={t("step5.motivation.questionTextPlaceholder")}
                                        rows={2}
                                    />
                                </Field>
                                <Field label={t("step5.motivation.expectedAnswer")}>
                                    <FormTextarea
                                        value={q.expectedAnswer}
                                        onChange={(e) => updateMotivationQuestion(i, { expectedAnswer: e.target.value })}
                                        placeholder={t("step5.motivation.expectedAnswerPlaceholder")}
                                        rows={2}
                                    />
                                </Field>
                                <Grid2>
                                    <Field label={t("step5.motivation.positiveSignals")}>
                                        <FormTextarea value={q.positiveSignals} onChange={(e) => updateMotivationQuestion(i, { positiveSignals: e.target.value })} placeholder={t("step5.motivation.positiveSignalsPlaceholder")} rows={2} />
                                    </Field>
                                    <Field label={t("step5.motivation.riskSignals")}>
                                        <FormTextarea value={q.riskSignals} onChange={(e) => updateMotivationQuestion(i, { riskSignals: e.target.value })} placeholder={t("step5.motivation.riskSignalsPlaceholder")} rows={2} />
                                    </Field>
                                </Grid2>
                                {false && (
                                    <Grid2>
                                        <WeightField value={q.weight} onChange={(v) => updateMotivationQuestion(i, { weight: v })} />
                                    </Grid2>
                                )}
                                <FormSwitch label={t("step5.motivation.canAnswerEliminate")} checked={q.canAnswerEliminate} onChange={(v) => updateMotivationQuestion(i, { canAnswerEliminate: v })} />
                                {scoringActive && (
                                    <>
                                        <Divider my={1} />
                                        <ScoringConfigPanel
                                            config={q.scoring}
                                            onChange={(scoring) => updateMotivationQuestion(i, { scoring })}
                                            labels={scoringLabels}
                                        />
                                    </>
                                )}
                            </Box>
                        </ItemCard>
                    ))}
                    <AddButton label={t("step5.motivation.add")} onClick={() => setFieldValue("motivationQuestions", [...motivationQuestions, createEmptyMotivationQuestion()])} />
                    <AdditionalInfoField value={sectionInfo.motivation} onChange={(e) => setSectionInfo("motivation", e.target.value)} />
                </Box>
            </CollapsibleSection>

            {/* Section 14: Financial expectations */}
            <CollapsibleSection
                number={14}
                title={t("step5.financial.title")}
                subtitle={t("step5.financial.subtitle")}
                icon={HiOutlineCurrencyDollar}
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={4}>
                    <FormSwitch label={t("step5.financial.aiAsks")} checked={financialExpectations.aiAsksAboutExpectations} onChange={(v) => setFinancial({ aiAsksAboutExpectations: v })} />
                    <Grid3>
                        <Field label={t("step5.financial.currency")} required invalid={invalid("financialExpectations.currency")} warning={warn("financialExpectations.currency")}>
                            <FormSelect value={financialExpectations.currency} onChange={(v) => setFinancial({ currency: v })} options={CURRENCY} />
                        </Field>
                    </Grid3>

                    <SubsectionTitle>{t("step5.financial.acceptableRangesTitle")}</SubsectionTitle>
                    <Grid2>
                        <Field label={t("step5.financial.acceptableFrom")}>
                            <FormInput type="number" value={financialExpectations.acceptableRangeFrom ?? ""} onChange={(e) => setFinancial({ acceptableRangeFrom: e.target.value === "" ? null : Number(e.target.value) })} placeholder={t("step5.financial.acceptableFromPlaceholder")} />
                        </Field>
                        <Field label={t("step5.financial.acceptableTo")}>
                            <FormInput type="number" value={financialExpectations.acceptableRangeTo ?? ""} onChange={(e) => setFinancial({ acceptableRangeTo: e.target.value === "" ? null : Number(e.target.value) })} placeholder={t("step5.financial.acceptableToPlaceholder")} />
                        </Field>
                        <Field label={t("step5.financial.preferredFrom")}>
                            <FormInput type="number" value={financialExpectations.preferredRangeFrom ?? ""} onChange={(e) => setFinancial({ preferredRangeFrom: e.target.value === "" ? null : Number(e.target.value) })} placeholder={t("step5.financial.preferredFromPlaceholder")} />
                        </Field>
                        <Field label={t("step5.financial.preferredTo")}>
                            <FormInput type="number" value={financialExpectations.preferredRangeTo ?? ""} onChange={(e) => setFinancial({ preferredRangeTo: e.target.value === "" ? null : Number(e.target.value) })} placeholder={t("step5.financial.preferredToPlaceholder")} />
                        </Field>
                    </Grid2>
                    <Field label={t("step5.financial.eliminatingAbove")}>
                        <FormInput type="number" value={financialExpectations.eliminatingAbove ?? ""} onChange={(e) => setFinancial({ eliminatingAbove: e.target.value === "" ? null : Number(e.target.value) })} placeholder={t("step5.financial.eliminatingAbovePlaceholder")} />
                    </Field>
                    <FormSwitch label={t("step5.financial.exceedingBudgetIsEliminating")} checked={financialExpectations.exceedingBudgetIsEliminating} onChange={(v) => setFinancial({ exceedingBudgetIsEliminating: v })} />
                    <AdditionalInfoField value={sectionInfo.financialExpectations} onChange={(e) => setSectionInfo("financialExpectations", e.target.value)} />

                    {scoringActive && (
                        <>
                            <Divider />
                            <ScoringConfigPanel config={financialExpectations.scoring} onChange={setFinancialScoring} labels={scoringLabels} />
                        </>
                    )}
                </Box>
            </CollapsibleSection>

            {/* Section 15: Availability */}
            <CollapsibleSection
                number={15}
                title={t("step5.availability.title")}
                subtitle={t("step5.availability.subtitle")}
                icon={HiOutlineCalendarDays}
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={4}>
                    <Grid3>
                        <Field label={t("step5.availability.earliestStart")} required={availability.aiAsksAboutAvailability} invalid={invalid("availability.earliestAcceptedStartDate")} warning={warn("availability.earliestAcceptedStartDate")}>
                            <FormInput type="date" value={availability.earliestAcceptedStartDate} onChange={(e) => setAvailability({ earliestAcceptedStartDate: e.target.value })} />
                        </Field>
                        <Field label={t("step5.availability.maxNoticePeriod")} required={availability.aiAsksAboutAvailability} invalid={invalid("availability.maxNoticePeriodDays")} warning={warn("availability.maxNoticePeriodDays")}>
                            <FormInput type="number" value={availability.maxNoticePeriodDays ?? ""} onChange={(e) => setAvailability({ maxNoticePeriodDays: e.target.value === "" ? null : Number(e.target.value) })} placeholder={t("step5.availability.maxNoticePeriodPlaceholder")} />
                        </Field>
                    </Grid3>

                    <SwitchGroup>
                        <FormSwitch label={t("step5.availability.aiAsks")} checked={availability.aiAsksAboutAvailability} onChange={(v) => setAvailability({ aiAsksAboutAvailability: v })} />
                        <FormSwitch label={t("step5.availability.lackEliminates")} checked={availability.lackOfQuickAvailabilityIsEliminating} onChange={(v) => setAvailability({ lackOfQuickAvailabilityIsEliminating: v })} />
                    </SwitchGroup>

                    <Divider />
                    <SubsectionTitle>{t("step5.availability.readinessTitle")}</SubsectionTitle>
                    <SwitchGroup>
                        <FormSwitch label={t("step5.availability.shiftWorkReadiness")} checked={availability.requiresShiftWorkReadiness} onChange={(v) => setAvailability({ requiresShiftWorkReadiness: v })} />
                        <FormSwitch label={t("step5.availability.weekendReadiness")} checked={availability.requiresWeekendReadiness} onChange={(v) => setAvailability({ requiresWeekendReadiness: v })} />
                        <FormSwitch label={t("step5.availability.businessTripReadiness")} checked={availability.requiresBusinessTripReadiness} onChange={(v) => setAvailability({ requiresBusinessTripReadiness: v })} />
                        <FormSwitch label={t("step5.availability.overtimeReadiness")} checked={availability.requiresOvertimeReadiness} onChange={(v) => setAvailability({ requiresOvertimeReadiness: v })} />
                    </SwitchGroup>
                    <AdditionalInfoField value={sectionInfo.availability} onChange={(e) => setSectionInfo("availability", e.target.value)} />

                    {scoringActive && (
                        <>
                            <Divider />
                            <ScoringConfigPanel config={availability.scoring} onChange={setAvailabilityScoring} labels={scoringLabels} />
                        </>
                    )}
                </Box>
            </CollapsibleSection>

            {/* Section 16: Disqualification rules */}
            <CollapsibleSection
                number={16}
                title={t("step5.disqualification.title")}
                subtitle={t("step5.disqualification.subtitle")}
                icon={HiOutlineNoSymbol}
                forceOpen={forceOpen}
            >
                <Box mb={4}>
                    <Text fontSize="xs" color="gray.500" mb={2}>{t("step3.equipment.quickAdd")}</Text>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        {DISQUALIFICATION_EXAMPLE_KEYS.map((key) => {
                            const label = t(`step5.disqualificationExamples.${key}`);
                            return (
                                <Box
                                    key={key}
                                    as="button"
                                    type="button"
                                    onClick={() => {
                                        const rule = createEmptyDisqualificationRule();
                                        rule.name = label;
                                        setFieldValue("disqualificationRules", [...disqualificationRules, rule]);
                                    }}
                                    px={2.5}
                                    py={1}
                                    borderRadius={6}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    fontSize="xs"
                                    color="gray.600"
                                    _hover={{ borderColor: "red.400", color: "red.600" }}
                                >
                                    + {label}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap={3}>
                    {disqualificationRules.map((rule, i) => (
                        <ItemCard key={i}>
                            <ItemHeader title={t("step5.disqualification.itemTitle")} index={i} badge={rule.name || undefined} onRemove={() => removeDisqualificationRule(i)} />
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Grid2>
                                    <Field label={t("step5.disqualification.name")} required invalid={invalid(`disqualificationRules.${i}.name`)} warning={warn(`disqualificationRules.${i}.name`)}>
                                        <FormInput value={rule.name} onChange={(e) => updateDisqualificationRule(i, { name: e.target.value })} placeholder={t("step5.disqualification.namePlaceholder")} />
                                    </Field>
                                    <Field label={t("step5.disqualification.relatedField")} hint={t("step5.disqualification.relatedFieldHint")}>
                                        <FormInput value={rule.relatedFormField} onChange={(e) => updateDisqualificationRule(i, { relatedFormField: e.target.value })} placeholder={t("step5.disqualification.relatedFieldPlaceholder")} />
                                    </Field>
                                </Grid2>
                                <Field label={t("step5.disqualification.conditionDescription")} required invalid={invalid(`disqualificationRules.${i}.conditionDescription`)} warning={warn(`disqualificationRules.${i}.conditionDescription`)}>
                                    <FormTextarea value={rule.conditionDescription} onChange={(e) => updateDisqualificationRule(i, { conditionDescription: e.target.value })} placeholder={t("step5.disqualification.conditionDescriptionPlaceholder")} rows={2} />
                                </Field>
                                <Field label={t("step5.disqualification.eliminatingValue")}>
                                    <FormInput value={rule.eliminatingValue} onChange={(e) => updateDisqualificationRule(i, { eliminatingValue: e.target.value })} placeholder={t("step5.disqualification.eliminatingValuePlaceholder")} />
                                </Field>
                                <SwitchGroup>
                                    <FormSwitch label={t("step5.disqualification.automaticElimination")} checked={rule.isAutomaticElimination} onChange={(v) => updateDisqualificationRule(i, { isAutomaticElimination: v })} />
                                    <FormSwitch label={t("step5.disqualification.requiresManualDecision")} checked={rule.requiresManualDecision} onChange={(v) => updateDisqualificationRule(i, { requiresManualDecision: v })} />
                                </SwitchGroup>
                                <Field label={t("step5.disqualification.recruiterComment")}>
                                    <FormTextarea value={rule.recruiterComment} onChange={(e) => updateDisqualificationRule(i, { recruiterComment: e.target.value })} placeholder={t("step5.disqualification.recruiterCommentPlaceholder")} rows={2} />
                                </Field>
                            </Box>
                        </ItemCard>
                    ))}
                    <AddButton label={t("step5.disqualification.add")} onClick={() => setFieldValue("disqualificationRules", [...disqualificationRules, createEmptyDisqualificationRule()])} />
                </Box>
            </CollapsibleSection>

            {/* Section 17: AI conversation config */}
            <CollapsibleSection
                number={17}
                title={t("step5.aiConfig.title")}
                subtitle={t("step5.aiConfig.subtitle")}
                icon={HiOutlineCpuChip}
                forceOpen={forceOpen}
            >
                <Box display="flex" flexDirection="column" gap={4}>
                    <Grid2>
                        <Field label={t("step5.aiConfig.conversationStyle")} required invalid={invalid("aiConversationConfig.conversationStyle")} warning={warn("aiConversationConfig.conversationStyle")}>
                            <FormSelect value={aiConversationConfig.conversationStyle} onChange={(v) => setAiConfig({ conversationStyle: v })} placeholder={t("step5.aiConfig.conversationStylePlaceholder")} options={CONVERSATION_STYLE} />
                        </Field>
                        <Field label={t("step5.aiConfig.conversationLanguage")} required invalid={invalid("aiConversationConfig.languages")} warning={warn("aiConversationConfig.languages")}>
                            <FormSelect multiple value={aiConversationConfig.languages} onChange={(v) => setAiConfig({ languages: v })} options={CONVERSATION_LANGUAGE} />
                        </Field>
                    </Grid2>

                    <Field label={t("step5.aiConfig.maxFollowUpQuestions")}>
                        <FormInput type="number" min="0" max="20" value={aiConversationConfig.maxFollowUpQuestions ?? ""} onChange={(e) => setAiConfig({ maxFollowUpQuestions: e.target.value === "" ? null : Number(e.target.value) })} placeholder={t("step5.aiConfig.maxFollowUpQuestionsPlaceholder")} />
                    </Field>

                    <SubsectionTitle>{t("step5.aiConfig.permissionsTitle")}</SubsectionTitle>
                    <SwitchGroup>
                        <FormSwitch label={t("step5.aiConfig.canAnswerSalary")} checked={aiConversationConfig.canAnswerAboutSalary} onChange={(v) => setAiConfig({ canAnswerAboutSalary: v })} />
                        <FormSwitch label={t("step5.aiConfig.canAnswerBenefits")} checked={aiConversationConfig.canAnswerAboutBenefits} onChange={(v) => setAiConfig({ canAnswerAboutBenefits: v })} />
                        <FormSwitch label={t("step5.aiConfig.canAnswerCompany")} checked={aiConversationConfig.canAnswerAboutCompany} onChange={(v) => setAiConfig({ canAnswerAboutCompany: v })} />
                    </SwitchGroup>

                    <Field label={t("step5.aiConfig.confidentialInfo")}>
                        <FormTextarea value={aiConversationConfig.confidentialInfo} onChange={(e) => setAiConfig({ confidentialInfo: e.target.value })} placeholder={t("step5.aiConfig.confidentialInfoPlaceholder")} rows={3} />
                    </Field>

                    <Field label={t("step5.aiConfig.fallbackAnswer")}>
                        <FormTextarea value={aiConversationConfig.fallbackAnswer} onChange={(e) => setAiConfig({ fallbackAnswer: e.target.value })} rows={2} />
                    </Field>
                </Box>
            </CollapsibleSection>

            {/* Section 18: Scoring (hidden — scoring not implemented yet) */}
            {false && (
            <CollapsibleSection number={18} title={t("step5.scoring.title")} subtitle={t("step5.scoring.subtitle")}>
                <Box display="flex" flexDirection="column" gap={5}>
                    <SubsectionTitle>{t("step5.scoring.globalSettingsTitle")}</SubsectionTitle>
                    <Grid3>
                        <Field label={t("step5.scoring.maxTotalPoints")}>
                            <FormInput type="number" value={scoringConfig.maxTotalPoints ?? ""} onChange={(e) => setFieldValue("scoringConfig.maxTotalPoints", e.target.value === "" ? null : Number(e.target.value))} />
                        </Field>
                        <Field label={t("step5.scoring.veryGoodMatchThreshold")}>
                            <FormInput type="number" min="0" max="100" value={scoringConfig.veryGoodMatchThreshold ?? ""} onChange={(e) => setFieldValue("scoringConfig.veryGoodMatchThreshold", e.target.value === "" ? null : Number(e.target.value))} placeholder={t("step5.scoring.veryGoodMatchThresholdPlaceholder")} />
                        </Field>
                        <Field label={t("step5.scoring.goodMatchThreshold")}>
                            <FormInput type="number" min="0" max="100" value={scoringConfig.goodMatchThreshold ?? ""} onChange={(e) => setFieldValue("scoringConfig.goodMatchThreshold", e.target.value === "" ? null : Number(e.target.value))} placeholder={t("step5.scoring.goodMatchThresholdPlaceholder")} />
                        </Field>
                    </Grid3>
                    <Grid2>
                        <Field label={t("step5.scoring.partialMatchThreshold")}>
                            <FormInput type="number" min="0" max="100" value={scoringConfig.partialMatchThreshold ?? ""} onChange={(e) => setFieldValue("scoringConfig.partialMatchThreshold", e.target.value === "" ? null : Number(e.target.value))} placeholder={t("step5.scoring.partialMatchThresholdPlaceholder")} />
                        </Field>
                    </Grid2>
                    <FormSwitch label={t("step5.scoring.eliminationBlocksRanking")} checked={scoringConfig.eliminationBlocksRanking} onChange={(v) => setFieldValue("scoringConfig.eliminationBlocksRanking", v)} />

                    <Divider />
                    <SubsectionTitle>{t("step5.scoring.categoryWeightsTitle")}</SubsectionTitle>
                    <Box borderRadius={12} border="1px solid" borderColor="border.primary" overflow="hidden">
                        {CATEGORY_MAX_POINTS_ROWS.map((row, idx) => (
                            <Box
                                key={row.key}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                px={4}
                                py={2.5}
                                borderBottom={idx < CATEGORY_MAX_POINTS_ROWS.length - 1 ? "1px solid" : "none"}
                                borderColor="border.primary"
                                bg={idx % 2 === 0 ? "transparent" : "bg.dark"}
                            >
                                <Text fontSize="sm" color="text.grey">{t(`step5.scoring.categories.${row.key}`)}</Text>
                                <FormInput
                                    type="number"
                                    min="0"
                                    w="80px"
                                    textAlign="right"
                                    value={scoringConfig.categoryMaxPoints[row.key]}
                                    onChange={(e) => setCategoryMaxPoints(row.key, e.target.value === "" ? 0 : Number(e.target.value))}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            </CollapsibleSection>
            )}
        </Box>
    );
};

export default Step5AIConfig;
